import { Shuffle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar } from '@/shared/ui';

/**
 * Minimalny czas trwania inscenizacji „Losowanie w toku”. API odpowiada
 * w ułamku sekundy — pasek celowo przeciąga moment, żeby losowanie było
 * wydarzeniem. Wynik pokazujemy dopiero, gdy minie ten czas i backend skończy.
 */
export const DRAW_SUSPENSE_MS = 10_000;

/** Sterowanie inscenizacją losowania — patrz {@link useDrawSuspense}. */
export interface DrawSuspense {
  /** Czy nakładka jest widoczna. */
  active: boolean;
  /** Postęp paska 0–100, biegnie lokalnym zegarem. */
  progress: number;
  /** Startuje inscenizację (wywołaj razem z mutacją losowania). */
  begin: () => void;
  /**
   * Kończy inscenizację nie wcześniej niż po {@link DRAW_SUSPENSE_MS} od
   * `begin()`; `done` odpala się po dogranym pasku (toast, nawigacja itp.).
   */
  finishAfterMinimum: (done: () => void) => void;
  /** Zdejmuje nakładkę od razu — na błąd losowania nie ma co czekać. */
  abort: () => void;
}

/**
 * Hook inscenizacji losowania: lokalny zegar paska (bez zapytań do backendu)
 * i domykanie z gwarantowanym minimalnym czasem trwania. Timery sprzątają się
 * przy odmontowaniu strony.
 */
export function useDrawSuspense(): DrawSuspense {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  // Ref, bo callbacki mutacji łapią stan z chwili kliknięcia — sprzed `begin()`.
  const startedAtRef = useRef<number | null>(null);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (startedAt === null) return;
    const timer = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - startedAt) / DRAW_SUSPENSE_MS) * 100));
    }, 100);
    return () => clearInterval(timer);
  }, [startedAt]);

  useEffect(
    () => () => {
      if (doneTimer.current !== null) clearTimeout(doneTimer.current);
    },
    [],
  );

  function begin(): void {
    const now = Date.now();
    startedAtRef.current = now;
    setProgress(0);
    setStartedAt(now);
  }

  function finishAfterMinimum(done: () => void): void {
    const started = startedAtRef.current ?? Date.now();
    const remaining = Math.max(0, DRAW_SUSPENSE_MS - (Date.now() - started));
    doneTimer.current = setTimeout(() => {
      startedAtRef.current = null;
      setStartedAt(null);
      done();
    }, remaining);
  }

  function abort(): void {
    startedAtRef.current = null;
    setStartedAt(null);
  }

  return { active: startedAt !== null, progress, begin, finishAfterMinimum, abort };
}

export interface DrawSuspenseOverlayProps {
  /** Postęp z {@link useDrawSuspense}. */
  progress: number;
  /** Podpis końcówki paska — zależny od tego, co się losuje (drabinka/grupy/terminarz). */
  finalCaption: string;
}

/**
 * Pełnoekranowa nakładka „Losowanie w toku” z paskiem postępu i podpisami
 * etapów. Etapy to czysta inscenizacja sterowana postępem.
 */
export function DrawSuspenseOverlay({ progress, finalCaption }: DrawSuspenseOverlayProps) {
  const { t } = useTranslation('tournament');

  const caption =
    progress < 40
      ? t('draw.progress.shuffle')
      : progress < 75
        ? t('draw.progress.seeds')
        : finalCaption;

  return (
    <div className="bg-overlay fixed inset-0 z-50 flex items-center justify-center p-6">
      <Card elevation="floating" className="w-full max-w-md">
        <div className="flex flex-col items-center gap-5 py-4 text-center">
          <Shuffle aria-hidden="true" className="text-brand-text size-10 animate-pulse" />
          <p className="text-fg-strong text-xl font-extrabold tracking-[-0.02em]">
            {t('draw.progress.title')}
          </p>
          <ProgressBar value={progress} max={100} className="w-full" />
          <p aria-live="polite" className="text-fg-muted text-sm">
            {caption}
          </p>
        </div>
      </Card>
    </div>
  );
}
